'use client'

import { MainBox } from '@opsynk/ui/components/main-box'
import { useModal } from '@opsynk/ui/components/modal-manager'
import { Label } from '@/components/ui/label'
import { Check, Cookie, Settings, X } from 'lucide-react'
import { useEffect, useState, type FC } from 'react'
import { Button } from './ui/button'
import { Switch } from './ui/switch'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslations } from 'next-intl'

export interface CookiePreferences {
  analytics: boolean
}

interface CookieConsentProps {
  onPreferencesChange?: (preferences: CookiePreferences) => void
}

const COOKIE_NAME = 'cookie-preferences'
const COOKIE_EXPIRY_DAYS = 365

export function CookieConsent({ onPreferencesChange }: CookieConsentProps) {
  const t = useTranslations('CookieConsent')
  const { openModal, closeModal } = useModal()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const savedPreferences = getCookie(COOKIE_NAME)
    if (!savedPreferences) {
      setTimeout(() => setIsVisible(true), 500)
    }
  }, [])

  const getCookie = (name: string): string | null => {
    if (typeof document === 'undefined') return null
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null
    return null
  }

  const setCookie = (name: string, value: string, days: number) => {
    if (typeof document === 'undefined') return
    const expires = new Date()
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
    if (process.env.NODE_ENV === 'production') {
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/; domain=.opsynk.com; Secure; SameSite=Lax`
    } else {
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`
    }
  }

  const handleAcceptAll = () => savePreferences({ analytics: true })
  const handleDenyAll = () => savePreferences({ analytics: false })
  const handleSaveCustom = (preferences: CookiePreferences) => {
    savePreferences(preferences)
    closeModal()
  }

  const savePreferences = (prefs: CookiePreferences) => {
    setCookie(COOKIE_NAME, JSON.stringify(prefs), COOKIE_EXPIRY_DAYS)
    onPreferencesChange?.(prefs)
    setIsVisible(false)
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          role="region"
          aria-labelledby="cookie-consent-title"
          className="fixed inset-x-0 bottom-0 z-50 sm:inset-x-auto sm:bottom-4 sm:left-4 sm:max-w-md"
        >
          <div className="border-t border-neutral-200 bg-white p-4 shadow-[0_-4px_24px_-8px_rgba(0,0,0,0.15)] sm:rounded-lg sm:border sm:p-5 sm:shadow-lg">
            <p id="cookie-consent-title" className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
              <Cookie aria-hidden className="h-4 w-4 text-primary" />
              {t('title')}
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-neutral-700">{t('description')}</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button onClick={handleAcceptAll} size="sm">
                <Check aria-hidden className="mr-1.5 h-4 w-4" />
                {t('acceptAll')}
              </Button>
              <Button
                onClick={handleDenyAll}
                size="sm"
                variant="secondary"
                className="border border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50"
              >
                <X aria-hidden className="mr-1.5 h-4 w-4" />
                {t('denyAll')}
              </Button>
            </div>
            <button
              type="button"
              onClick={() => openModal(() => <CookieModal closeModal={closeModal} handleSaveCustom={handleSaveCustom} />)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-sm text-sm font-medium text-neutral-700 underline-offset-4 hover:text-neutral-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Settings aria-hidden className="h-4 w-4" />
              {t('customize')}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

interface CookieModalProps {
  closeModal: () => void
  handleSaveCustom: (preferences: CookiePreferences) => void
}

const CookieModal: FC<CookieModalProps> = ({ closeModal, handleSaveCustom }) => {
  const t = useTranslations('CookieConsent')

  const [preferences, setPreferences] = useState<CookiePreferences>({ analytics: false })

  const handlePreferenceChange = (key: keyof CookiePreferences, value: boolean) => {
    setPreferences((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <MainBox>
      <div className="flex items-center gap-2 mb-4">
        <Settings className="h-5 w-5" />
        <span>{t('modalTitle')}</span>
      </div>

      <div className="space-y-6 py-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 gap-8 border rounded-lg hover:bg-accent/50 transition-colors">
            <div className="space-y-1">
              <Label className="text-sm font-medium">{t('analyticsLabel')}</Label>
              <p className="text-xs text-muted-foreground">{t('analyticsDescription')}</p>
            </div>
            <Switch
              checked={preferences.analytics}
              onCheckedChange={(checked) => handlePreferenceChange('analytics', checked)}
              className="data-[state=checked]:bg-primary"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button onClick={() => handleSaveCustom(preferences)} className="flex-1 text-white bg-primary hover:bg-primary/90">
            {t('savePreferences')}
          </Button>
          <Button variant="outline" onClick={closeModal} className="flex-1">
            {t('cancel')}
          </Button>
        </div>
      </div>
    </MainBox>
  )
}
